// =====================================================================
// src/pages/intl-acca/TournamentHubPage.tsx
// =====================================================================
// 4-tab Tournament Hub: Groups / Calendar / Bracket / DNA Roster.
// Pure presentational — host wires data via the contract at the bottom.
//
// Routes mounted at: /intl-acca/:tournamentId/hub
//
// NOTE on data availability:
//   * Groups, fixtures, bracket — depend on a future Match entity
//     (intl_acca_matches). Until that migration lands, hosts supply
//     these from an interim fixture cache (the structure here mirrors
//     what the future RPC will return).
//   * Bracket placeholders ('?' team codes) render as "TBD" pills —
//     never a fabricated matchup.

import { useState } from "react";
import type {
  IntlAccaTournament,
} from "../../db/intl-acca-types";
import type {
  GroupStanding,
  IntlAccaMatch,
  Nation,
} from "../../db/intl-acca-types-extended";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { GroupStandingsCard } from "../../components/intl-acca/GroupStandingsCard";
import { FixtureRow } from "../../components/intl-acca/FixtureRow";
import { NationFlag } from "../../components/intl-acca/NationFlag";

export interface TournamentHubPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  /** All groups for this tournament. Empty array allowed (PRE_TOURNAMENT). */
  groups: GroupStanding[];
  /** All matches across the tournament — host filters by tab as needed. */
  matches: IntlAccaMatch[];
  /** Nation reference lookup. */
  nations: Record<string, Nation>;
  /** All competing nation codes for the DNA roster tab. */
  participatingNations: string[];
  /** Bracket entries — host computes from R16-onwards matches. */
  bracket: BracketState;
  onTeamClick?: (teamCode: string) => void;
  /** Fixture row click — host wires to the slip detail when a slip exists. */
  onFixtureClick?: (matchId: string) => void;
}

export interface BracketMatchup {
  a: string | null;            // Nation.code, or null until the slot resolves
  b: string | null;
  /** Published slot descriptor shown while the nation is unresolved. */
  a_slot?: string | null;
  b_slot?: string | null;
  is_final?: boolean;
}
export interface BracketState {
  r32: BracketMatchup[];       // 16 entries (48-team WC)
  r16: BracketMatchup[];       // 8 entries
  qf: BracketMatchup[];        // 4 entries
  sf: BracketMatchup[];        // 2 entries
  final: BracketMatchup[];     // 1 entry
}

type TabId = "groups" | "calendar" | "bracket" | "dna";
const TABS: Array<{ id: TabId; label: string }> = [
  { id: "groups",   label: "GROUPS · STANDINGS" },
  { id: "calendar", label: "FULL CALENDAR" },
  { id: "bracket",  label: "KNOCKOUT BRACKET" },
  { id: "dna",      label: "DNA ROSTER" },
];

export function TournamentHubPage(props: TournamentHubPageProps): JSX.Element {
  const {
    loading, error, tournament, groups, matches, nations,
    participatingNations, bracket, onTeamClick, onFixtureClick,
  } = props;
  const [tab, setTab] = useState<TabId>("groups");

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading tournament hub…</div>
      </div>
    );
  }
  if (error || !tournament) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Hub unavailable."
          body={error ?? "Tournament not found."}
        />
      </div>
    );
  }

  return (
    <div className="ia-root">
      <TournamentStateBanner tournament={tournament} />

      <header className="ia-page-header">
        <div>
          <div className="ia-eyebrow">
            {tournament.name} · {tournament.region} · Tournament Hub
          </div>
          <h1>
            {tournament.name}.{" "}
            <em>{participatingNations.length} nations, one engine.</em>
          </h1>
          <p className="ia-sub">
            Live standings, the full calendar, and the knockout bracket as
            it forms. Click any team for its DNA profile; click any fixture
            for the slip context.
          </p>
        </div>
      </header>

      <nav className="ia-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`ia-tab${tab === t.id ? " active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="ia-page-pad">
        {tab === "groups" && (
          <GroupsTab groups={groups} nations={nations} onTeamClick={onTeamClick} />
        )}
        {tab === "calendar" && (
          <CalendarTab matches={matches} nations={nations} onFixtureClick={onFixtureClick} />
        )}
        {tab === "bracket" && (
          <BracketTab bracket={bracket} nations={nations} />
        )}
        {tab === "dna" && (
          <DNARosterTab
            nations={nations}
            codes={participatingNations}
            onTeamClick={onTeamClick}
          />
        )}
      </div>
    </div>
  );
}

// ─── Groups tab ───────────────────────────────────────────────────
function GroupsTab({
  groups, nations, onTeamClick,
}: {
  groups: GroupStanding[];
  nations: Record<string, Nation>;
  onTeamClick?: (code: string) => void;
}): JSX.Element {
  if (groups.length === 0) return <HonestEmptyState variant="custom" title="Groups not drawn yet." body="Group standings populate after the draw + first matchday." />;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
        gap: 20,
      }}
    >
      {groups.map((g) => (
        <GroupStandingsCard key={g.group_id} group={g} nations={nations} onTeamClick={onTeamClick} />
      ))}
    </div>
  );
}

// ─── Calendar tab ─────────────────────────────────────────────────
function CalendarTab({
  matches, nations, onFixtureClick,
}: {
  matches: IntlAccaMatch[];
  nations: Record<string, Nation>;
  onFixtureClick?: (matchId: string) => void;
}): JSX.Element {
  if (matches.length === 0) {
    return (
      <HonestEmptyState
        variant="custom"
        title="No fixtures published yet."
        body="Fixtures land once the draw is complete. Empty by design until then."
      />
    );
  }
  // Group by date
  const byDate = new Map<string, IntlAccaMatch[]>();
  matches.forEach((m) => {
    const key = m.kickoff ? new Date(m.kickoff).toISOString().slice(0, 10) : "TBD";
    const arr = byDate.get(key) ?? [];
    arr.push(m);
    byDate.set(key, arr);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      {Array.from(byDate.entries()).map(([date, list]) => (
        <section key={date}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 14,
              marginBottom: 14,
            }}
          >
            <span
              style={{
                fontFamily: "var(--sz-font-display)",
                fontSize: 30,
                letterSpacing: "0.04em",
              }}
            >
              {date === "TBD"
                ? "Date TBD"
                : new Date(date).toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
            </span>
            <span
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-xs)",
                color: "var(--text-tertiary)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              {list.length} fixtures
            </span>
          </div>
          <div className="ia-card" style={{ padding: 0 }}>
            {list.map((m) => (
              <FixtureRow
                key={m.match_id}
                match={m}
                nations={nations}
                onSelect={() => onFixtureClick?.(m.match_id)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ─── Bracket tab ──────────────────────────────────────────────────
function BracketTab({
  bracket, nations,
}: {
  bracket: BracketState;
  nations: Record<string, Nation>;
}): JSX.Element {
  return (
    <div>
      <div className="ia-bracket">
        <BracketCol title="ROUND OF 32" matches={bracket.r32} nations={nations} />
        <BracketCol title="ROUND OF 16" matches={bracket.r16} nations={nations} />
        <BracketCol title="QUARTERFINALS" matches={bracket.qf} nations={nations} />
        <BracketCol title="SEMIFINALS" matches={bracket.sf} nations={nations} />
        <BracketCol title="FINAL" matches={bracket.final} nations={nations} />
      </div>
      <div
        style={{
          marginTop: 24,
          padding: 16,
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderLeft: "2px solid var(--accent)",
          fontSize: "var(--sz-body-sm)",
          color: "var(--text-secondary)",
          lineHeight: 1.6,
          borderRadius: "var(--sz-radius-lg)",
        }}
      >
        <span
          className="ia-mono"
          style={{
            color: "var(--accent)",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
        >
          Engine note ·
        </span>{" "}
        The full published bracket (dates, venues, slot paths) is shown now;
        each slot displays its official descriptor ("Winner Group E") until
        group results resolve the nation. STRIKZA pricing treats knockouts as
        a separate variable stack and only prices a tie once both teams are
        determined.
      </div>
    </div>
  );
}

function BracketCol({
  title, matches, nations,
}: {
  title: string;
  matches: BracketMatchup[];
  nations: Record<string, Nation>;
}): JSX.Element {
  return (
    <div className="ia-bracket-col">
      <div className="ia-bracket-col-title">{title}</div>
      {matches.map((m, i) => (
        <div
          key={i}
          className={`ia-bracket-match${m.is_final ? " final" : ""}`}
        >
          <BracketTeam code={m.a} slot={m.a_slot} nations={nations} />
          <div
            style={{ borderTop: "1px solid var(--border-faint)", margin: "6px 0" }}
          />
          <BracketTeam code={m.b} slot={m.b_slot} nations={nations} />
        </div>
      ))}
    </div>
  );
}

function BracketTeam({
  code, slot, nations,
}: {
  code: string | null;
  slot?: string | null;
  nations: Record<string, Nation>;
}): JSX.Element {
  if (!code || code === "?") {
    // Unresolved slot: show the real published descriptor
    // ("Winner Group E"), not a blank "TBD", when we have it.
    return (
      <div className="ia-bracket-team tbd">
        <span
          className="ia-mono"
          style={{
            fontSize: 10.5,
            letterSpacing: "0.10em",
            color: "var(--text-tertiary)",
          }}
        >
          {slot && slot.trim() ? slot : "TBD"}
        </span>
      </div>
    );
  }
  const n = nations[code] ?? null;
  return (
    <div className="ia-bracket-team">
      <NationFlag nation={n} size={18} />
      <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
        {n?.short ?? code}
      </span>
    </div>
  );
}

// ─── DNA roster tab ───────────────────────────────────────────────
function DNARosterTab({
  nations, codes, onTeamClick,
}: {
  nations: Record<string, Nation>;
  codes: string[];
  onTeamClick?: (code: string) => void;
}): JSX.Element {
  if (codes.length === 0) return <HonestEmptyState variant="tournament-empty" />;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        gap: 12,
      }}
    >
      {codes.map((code) => {
        const n = nations[code] ?? null;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onTeamClick?.(code)}
            className="ia-card"
            style={{
              padding: 16,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 14,
              textAlign: "left",
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
            }}
          >
            <NationFlag nation={n} size={42} />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "var(--sz-font-display)",
                  fontSize: 18,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  color: "var(--text)",
                }}
              >
                {n?.short ?? code}
              </div>
              <div
                style={{
                  fontSize: "var(--sz-body-sm)",
                  color: "var(--text-secondary)",
                  marginTop: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {n?.name ?? code}
              </div>
              {n?.is_host ? (
                <div
                  className="ia-mono"
                  style={{
                    fontSize: 9,
                    color: "var(--accent)",
                    letterSpacing: "0.18em",
                    marginTop: 4,
                  }}
                >
                  ★ HOST
                </div>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: tournament } = await sb.from('intl_acca_tournaments')
//                                    .select('*').eq('id', tid).single();
//
// // Future: SELECT from intl_acca_group_standings WHERE tournament_id = $1
// // Until that view exists, host computes from intl_acca_matches.
// const groups: GroupStanding[] = computeStandings(matches);
//
// // Future: SELECT from intl_acca_matches WHERE tournament_id = $1
// const matches: IntlAccaMatch[] = await fetchMatches(tid);
//
// // Future: a view that returns nations participating in a tournament.
// const participatingNations: string[] = Array.from(new Set(matches.flatMap(m => [m.home, m.away])));
//
// // bracket: derived from knockout rows of matches.
// const bracket: BracketState = computeBracket(matches);
